// Mudar de plano: planos públicos com o preço no período atual da
// assinatura e o aviso de upgrade/downgrade. Upgrade só vale depois de
// pago: a resposta traz a fatura UPGRADE com `pix` (mostra o QR aqui e
// espera o socket) ou `charge` (cartão salvo). Downgrade fica agendado
// para o fim do ciclo. Escolher o plano atual com downgrade agendado desfaz.
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import { makeStyles, alpha } from "@material-ui/core/styles";

import AerosDialog from "../AerosDialog";
import PixPanel, { AlreadyPaid } from "../SubscriptionCheckout/PixPanel";
import { formatBRL } from "../SubscriptionCheckout/checkoutRules";
import ManageFooter from "./ManageFooter";
import Notice from "./Notice";
import { AuthContext } from "../../context/Auth/AuthContext";
import useSubscription from "../../hooks/useSubscription";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { avisoTrocaPlano, periodLabel, precoNoPeriodo } from "./subscriptionCardRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  return {
    list: { display: "flex", flexDirection: "column", gap: 8 },
    option: {
      display: "grid",
      gridTemplateColumns: "auto minmax(0,1fr) auto",
      alignItems: "center",
      gap: 12,
      minHeight: 44,
      width: "100%",
      textAlign: "left",
      padding: "10px 14px",
      borderRadius: 12,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
      color: theme.palette.text.primary,
      font: "inherit",
      cursor: "pointer",
      touchAction: "manipulation",
      transition: "border-color 150ms ease-out, box-shadow 150ms ease-out",
      "&:focus-visible": { outline: `2px solid ${AEROS.cyan}`, outlineOffset: 2 },
      "&:disabled": { cursor: "default" },
    },
    on: {
      borderColor: alpha("#A044FF", 0.6),
      boxShadow: `0 0 0 1px ${alpha("#A044FF", 0.25)}`,
    },
    radio: {
      width: 14,
      height: 14,
      borderRadius: "50%",
      border: `2px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.25)"}`,
    },
    radioOn: {
      borderColor: theme.palette.primary.main,
      background: `radial-gradient(circle, ${theme.palette.primary.main} 45%, transparent 50%)`,
    },
    name: {
      fontSize: 13.5,
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
    },
    limits: { fontSize: 11.5, color: theme.palette.text.secondary, marginTop: 2 },
    price: {
      fontSize: 15,
      fontWeight: 800,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
      "& small": { fontSize: 11, fontWeight: 500, color: theme.palette.text.secondary },
    },
    tag: {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      borderRadius: 6,
      padding: "2px 7px",
      color: stateText(theme, "#C08BFF", "purple"),
      backgroundColor: alpha("#A044FF", 0.14),
    },
    pixValue: {
      fontSize: 13,
      color: theme.palette.text.secondary,
      textAlign: "center",
      fontVariantNumeric: "tabular-nums",
    },
    empty: { fontSize: 13, color: theme.palette.text.secondary },
  };
});

const ChangePlanDialog = ({
  open,
  onClose,
  subscription,
  plans,
  currentPlan,
  currentPrice,
  cardLabel,
  onDone,
  onUpgradeOpen,
}) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const { user, socket } = useContext(AuthContext);
  const { patchMe, payInvoice, me } = useSubscription();
  const period = subscription?.period || "MENSAL";
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  // Upgrade no Pix: { invoice, pix } enquanto espera o pagamento.
  const [upgrade, setUpgrade] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected(null);
      setUpgrade(null);
    }
  }, [open]);

  // Planos com preço no período atual; o plano vigente (mesmo privado)
  // aparece primeiro, marcado como "Atual".
  const options = useMemo(() => {
    const lista = (plans || [])
      .map((p) => ({ ...p, price: precoNoPeriodo(p, period) }))
      .filter((p) => p.price > 0 && p.id !== currentPlan?.id);
    const atual = currentPlan ? [{ ...currentPlan, price: currentPrice }] : [];
    return [...atual, ...lista];
  }, [plans, period, currentPlan, currentPrice]);

  const escolhido = options.find((p) => p.id === selected) || null;
  const ehAtual = escolhido && escolhido.id === subscription?.planId;
  const desfaz = ehAtual && !!subscription?.pendingPlanId;
  const tipo = escolhido && !ehAtual ? avisoTrocaPlano(currentPrice, escolhido.price).tipo : null;
  const fimDoCiclo = subscription?.cycleEnd
    ? moment(subscription.cycleEnd).format("DD/MM/YYYY")
    : "—";

  // Pix do upgrade: fecha quando o pagamento confirma (uma vez só).
  const doneRef = useRef(false);
  const companyId = user?.companyId;
  useEffect(() => {
    if (!upgrade?.pix || !socket || !companyId) return undefined;
    doneRef.current = false;
    const onEvent = (data) => {
      if (data?.action !== "CONCLUIDA" || doneRef.current) return;
      doneRef.current = true;
      onDone(i18n.t("subscription.manage.planUpgradePaid"));
    };
    const paymentEvent = `company-${companyId}-payment`;
    const subscriptionEvent = `company-${companyId}-subscription`;
    socket.on(paymentEvent, onEvent);
    socket.on(subscriptionEvent, onEvent);
    return () => {
      socket.off(paymentEvent, onEvent);
      socket.off(subscriptionEvent, onEvent);
    };
  }, [upgrade, socket, companyId, onDone]);

  const handleConfirm = async () => {
    if (!escolhido) return;
    setSaving(true);
    try {
      const data = await patchMe({ action: "changePlan", planId: escolhido.id });
      if (data?.pix && data?.invoice) {
        setUpgrade({ invoice: data.invoice, pix: data.pix, planId: escolhido.id });
        return;
      }
      if (!data?.invoice) {
        // Downgrade agendado, desfazer, ou upgrade sem nada a cobrar.
        if (desfaz) onDone(i18n.t("subscription.manage.planUndone"));
        else if (tipo === "downgrade")
          onDone(i18n.t("subscription.manage.planDowngradeScheduled", { date: fimDoCiclo }));
        else onDone(i18n.t("subscription.manage.planChanged"));
        return;
      }
      const status = data?.charge?.status;
      if (status === "approved") {
        onDone(i18n.t("subscription.manage.planUpgradePaid"));
      } else if (status && status !== "rejected") {
        onDone(i18n.t("subscription.manage.planUpgradeReview"), "info");
      } else {
        // Recusado ou sem cartão cobrável: a fatura UPGRADE ficou aberta.
        onDone(i18n.t("subscription.manage.planUpgradeOpen"), "warn");
        if (onUpgradeOpen) onUpgradeOpen(data.invoice);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleRegeneratePix = async () => {
    setGenerating(true);
    try {
      const data = await payInvoice(upgrade.invoice.id, { method: "PIX" });
      if (data?.pix) setUpgrade((prev) => ({ ...prev, pix: data.pix }));
    } catch (err) {
      toastError(err);
    } finally {
      setGenerating(false);
    }
  };

  const handleAlreadyPaid = async () => {
    setChecking(true);
    try {
      const data = await me();
      if (data?.subscription?.planId === upgrade.planId && !doneRef.current) {
        doneRef.current = true;
        onDone(i18n.t("subscription.manage.planUpgradePaid"));
      } else {
        toast.info(i18n.t("subscription.checkout.stillPending"));
      }
    } catch (err) {
      toastError(err);
    } finally {
      setChecking(false);
    }
  };

  if (upgrade) {
    const value = formatBRL(upgrade.invoice.value);
    return (
      <AerosDialog
        open={open}
        onClose={onClose}
        title={i18n.t("subscription.manage.planPixTitle")}
        subtitle={escolhido?.name}
        width={460}
        footer={
          <ManageFooter cancelLabel={i18n.t("subscription.manage.close")} onCancel={onClose} />
        }
      >
        <div className={classes.pixValue}>
          {i18n.t("subscription.manage.planPixValue", { value })}
        </div>
        <PixPanel
          pix={upgrade.pix}
          submitLabel={i18n.t("subscription.checkout.generatePix", { value })}
          submitClassName={m.submitButton}
          generating={generating}
          onGenerate={handleRegeneratePix}
          alreadyPaid={<AlreadyPaid onClick={handleAlreadyPaid} checking={checking} />}
        />
      </AerosDialog>
    );
  }

  const aviso = (() => {
    if (desfaz) return <Notice live>{i18n.t("subscription.manage.planUndo")}</Notice>;
    if (tipo === "upgrade") {
      const meio =
        subscription?.paymentMethod === "PIX"
          ? i18n.t("subscription.manage.planUpgradePix")
          : cardLabel
          ? i18n.t("subscription.manage.planUpgradeCard", { card: cardLabel })
          : "";
      return (
        <Notice live>
          {i18n.t("subscription.manage.planUpgrade")} {meio}
        </Notice>
      );
    }
    if (tipo === "downgrade") {
      return (
        <Notice live>
          {i18n.t("subscription.manage.planDowngrade", { date: fimDoCiclo })}
        </Notice>
      );
    }
    return null;
  })();

  const suffix =
    period === "ANUAL"
      ? i18n.t("subscription.checkout.perYear")
      : i18n.t("subscription.checkout.perMonth");

  return (
    <AerosDialog
      open={open}
      onClose={onClose}
      title={i18n.t("subscription.manage.planTitle")}
      subtitle={i18n.t("subscription.manage.planSubtitle", {
        period: periodLabel(period).toLowerCase(),
      })}
      width={560}
      loading={saving}
      footer={
        <ManageFooter
          cancelLabel={i18n.t("aeros.flow.cancel")}
          onCancel={onClose}
          submitLabel={i18n.t("subscription.manage.planConfirm")}
          onSubmit={handleConfirm}
          submitDisabled={!escolhido || (ehAtual && !desfaz) || tipo === "same"}
          loading={saving}
        />
      }
    >
      <div
        role="radiogroup"
        aria-label={i18n.t("subscription.manage.planListLabel")}
        className={classes.list}
      >
        {options.map((p) => {
          const on = p.id === selected;
          const atual = p.id === subscription?.planId;
          const agendado = p.id === subscription?.pendingPlanId;
          return (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={on}
              disabled={saving}
              className={`${classes.option} ${on ? classes.on : ""}`}
              onClick={() => setSelected(p.id)}
            >
              <span className={`${classes.radio} ${on ? classes.radioOn : ""}`} />
              <span style={{ minWidth: 0 }}>
                <span className={classes.name}>
                  {p.name}
                  {atual && (
                    <span className={classes.tag}>{i18n.t("subscription.manage.planCurrent")}</span>
                  )}
                  {agendado && (
                    <span className={classes.tag}>
                      {i18n.t("subscription.manage.planScheduled")}
                    </span>
                  )}
                </span>
                {p.users != null && (
                  <span className={classes.limits} style={{ display: "block" }}>
                    {i18n.t("subscription.manage.planLimits", {
                      users: p.users,
                      connections: p.connections,
                      queues: p.queues,
                    })}
                  </span>
                )}
              </span>
              <span className={classes.price}>
                {formatBRL(p.price)}
                <small>{suffix}</small>
              </span>
            </button>
          );
        })}
        {options.length <= 1 && (
          <div className={classes.empty}>{i18n.t("subscription.manage.planNoOthers")}</div>
        )}
      </div>
      {aviso}
    </AerosDialog>
  );
};

export default ChangePlanDialog;
